import type { BulkPaymentFeesFilters } from "@/hera-api/bulk-payment-api";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import { useCallback, useState } from "react";

const EMPTY_FILTERS: Required<BulkPaymentFeesFilters> = {
  jurisdictionDisplayCodes: [],
  licenseTypeIds: [],
  registrantAccountId: null,
  amsCompanyName: null,
  amsQuestionId: null,
};

function readDashboardFilters(): Required<BulkPaymentFeesFilters> {
  return {
    jurisdictionDisplayCodes: sessionStorage.get<string[]>(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_JURISDICTIONS,
      [],
    ),
    licenseTypeIds: sessionStorage.get<string[]>(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_LICENSE_TYPES,
      [],
    ),
    registrantAccountId: sessionStorage.get<string | null>(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_REGISTRANT_ACCOUNT_ID,
      null,
    ),
    amsCompanyName: sessionStorage.get<string | null>(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_AMS,
      null,
    ),
    amsQuestionId: sessionStorage.get<string | null>(
      SESSION_KEYS.REGISTRANT_DASHBOARD_AMS_QUESTION_ID,
      null,
    ),
  };
}

export function hasBulkPaymentFilters(
  filters: Required<BulkPaymentFeesFilters>,
): boolean {
  return (
    filters.jurisdictionDisplayCodes.length > 0 ||
    filters.licenseTypeIds.length > 0 ||
    !!filters.registrantAccountId ||
    !!filters.amsCompanyName
  );
}

/**
 * Snapshot of the registrant dashboard's global filters for the bulk payment
 * screen. Read once on mount so the screen shows the same properties the
 * dashboard did, and so refining the on-screen AMS picker does not refetch.
 */
export function useBulkPaymentDashboardFilters() {
  const [filters, setFilters] =
    useState<Required<BulkPaymentFeesFilters>>(readDashboardFilters);

  /** Widen this screen to every payable property, and the dashboard with it. */
  const clearFilters = useCallback(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_JURISDICTIONS,
      [],
    );
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_LICENSE_TYPES,
      [],
    );
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_REGISTRANT_ACCOUNT_ID,
      null,
    );
    sessionStorage.set(SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_AMS, null);
    setFilters(EMPTY_FILTERS);
  }, []);

  return {
    filters,
    hasFilters: hasBulkPaymentFilters(filters),
    clearFilters,
  };
}
